import React, { useState, useEffect } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import { cn } from '@/admin/lib/utils';

interface CollapsibleSectionProps {
  id: string;
  title: string;
  description?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
  headerActions?: React.ReactNode;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  id,
  title,
  description,
  defaultOpen = true,
  children,
  className,
  headerActions,
}) => {
  const storageKey = `skill-section-${id}`;

  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === 'undefined') return defaultOpen;
    const stored = localStorage.getItem(storageKey);
    return stored !== null ? stored === 'true' : defaultOpen;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, String(isOpen));
  }, [isOpen, storageKey]);

  return (
    <div
      className={cn(
        'bg-card rounded-xl border shadow-sm overflow-hidden',
        className
      )}
    >
      {/* Header - use div with role to avoid button nesting */}
      <div className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors">
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
          className="flex-1 flex items-center gap-4 cursor-pointer"
        >
          <div className="flex flex-col gap-0.5">
            <h2 className="text-sm font-semibold">{title}</h2>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-1 rounded hover:bg-muted/50 transition-colors"
            aria-label={isOpen ? 'Collapse section' : 'Expand section'}
          >
            <IconChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        </div>
      </div>

      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t px-6 py-5">{children}</div>
        </div>
      </div>
    </div>
  );
};
